import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc, collection, getDocs, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../firebase';
import { 
  Building, 
  Mail, 
  Phone, 
  MapPin, 
  Clock, 
  Globe, 
  DollarSign, 
  Store, 
  Save, 
  Edit2, 
  X, 
  Plus,
  Settings,
  Crown,
  Package,
  Truck
} from 'lucide-react';
import './AdminBusinessProfile.css';

function AdminBusinessProfile({ user }) {
  const [activeTab, setActiveTab] = useState('business');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  
  // Business Information
  const [businessInfo, setBusinessInfo] = useState({
    name: '',
    email: '',
    phone: '',
    address: {
      line1: '',
      line2: '',
      city: 'New York',
      state: 'NY',
      postalCode: '',
      country: 'United States'
    },
    website: '',
    taxRate: 0.08875, // 8.875% NYC sales tax
    currency: 'USD',
    timezone: 'America/New_York'
  });

  // Pickup Locations
  const [pickupLocations, setPickupLocations] = useState([]);
  const [editingLocation, setEditingLocation] = useState(null);
  const [locationForm, setLocationForm] = useState({
    name: '',
    address: '',
    phone: '',
    hours: '',
    estimatedReady: '',
    icon: '🏪',
    active: true
  });

  // Business Settings
  const [businessSettings, setBusinessSettings] = useState({
    freeShippingThreshold: 0,
    defaultShippingRate: 25,
    enablePickup: true,
    enableShipping: true,
    businessHours: 'Mon-Sat: 9AM-6PM, Sun: 10AM-4PM',
    orderProcessingTime: '1-2 business days',
    returnPolicy: '30-day return policy',
    refundPolicy: 'Full refund within 30 days',
    contactEmail: 'orders@trinitees.com',
    supportPhone: '(868) 987-6543'
  });

  useEffect(() => {
    loadBusinessProfile();
    loadPickupLocations();
    loadBusinessSettings();
  }, [user]);

  const loadBusinessProfile = async () => {
    try {
      const businessDoc = await getDoc(doc(db, 'businessProfile', 'main'));
      if (businessDoc.exists()) {
        setBusinessInfo(businessDoc.data());
      }
    } catch (error) {
      console.error('Error loading business profile:', error);
    }
  };

  const loadPickupLocations = async () => {
    try {
      const pickupSnapshot = await getDocs(collection(db, 'pickupLocations'));
      const locations = pickupSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setPickupLocations(locations);
    } catch (error) {
      console.error('Error loading pickup locations:', error);
    }
  };

  const loadBusinessSettings = async () => {
    try {
      const settingsDoc = await getDoc(doc(db, 'businessSettings', 'main'));
      if (settingsDoc.exists()) {
        setBusinessSettings(settingsDoc.data());
      }
    } catch (error) {
      console.error('Error loading business settings:', error);
    }
  };

  const saveBusinessInfo = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'businessProfile', 'main'), {
        ...businessInfo,
        updatedAt: serverTimestamp()
      });
      showMessage('Business information updated successfully!', 'success');
    } catch (error) {
      console.error('Error saving business info:', error);
      showMessage('Error saving business information', 'error');
    }
    setLoading(false);
  };

  const saveBusinessSettings = async () => {
    setLoading(true);
    try {
      await updateDoc(doc(db, 'businessSettings', 'main'), {
        ...businessSettings,
        updatedAt: serverTimestamp()
      });
      showMessage('Business settings updated successfully!', 'success');
    } catch (error) {
      console.error('Error saving business settings:', error);
      showMessage('Error saving business settings', 'error');
    }
    setLoading(false);
  };

  const savePickupLocation = async () => {
    setLoading(true);
    try {
      const locationData = {
        ...locationForm,
        updatedAt: serverTimestamp()
      };

      if (editingLocation) {
        await updateDoc(doc(db, 'pickupLocations', editingLocation.id), locationData);
        showMessage('Pickup location updated successfully!', 'success');
      } else {
        await setDoc(doc(db, 'pickupLocations', `location_${Date.now()}`), locationData);
        showMessage('Pickup location added successfully!', 'success');
      }

      resetLocationForm();
      loadPickupLocations();
    } catch (error) {
      console.error('Error saving pickup location:', error);
      showMessage('Error saving pickup location', 'error');
    }
    setLoading(false);
  };

  const deletePickupLocation = async (locationId) => {
    if (!window.confirm('Are you sure you want to delete this pickup location?')) return;

    try {
      await updateDoc(doc(db, 'pickupLocations', locationId), {
        active: false,
        deletedAt: serverTimestamp()
      });
      showMessage('Pickup location deleted successfully!', 'success');
      loadPickupLocations();
    } catch (error) {
      console.error('Error deleting pickup location:', error);
      showMessage('Error deleting pickup location', 'error');
    }
  };

  const resetLocationForm = () => {
    setLocationForm({
      name: '',
      address: '',
      phone: '',
      hours: '',
      estimatedReady: '',
      icon: '🏪',
      active: true
    });
    setEditingLocation(null);
  };

  const editPickupLocation = (location) => {
    setLocationForm(location);
    setEditingLocation(location);
  };

  const showMessage = (text, type) => {
    setMessage(text);
    setMessageType(type);
    setTimeout(() => setMessage(''), 5000);
  };

  return (
    <div className="admin-business-profile">
      <div className="profile-header">
        <div className="header-content">
          <div className="header-title">
            <Crown size={32} />
            <div>
              <h1>Business Profile</h1>
              <p>Manage your business information and settings</p>
            </div>
          </div>
        </div>
      </div>

      <div className="profile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'business' ? 'active' : ''}`}
          onClick={() => setActiveTab('business')}
        >
          <Building size={20} />
          Business Info
        </button>
        <button 
          className={`tab-btn ${activeTab === 'pickup' ? 'active' : ''}`}
          onClick={() => setActiveTab('pickup')}
        >
          <Store size={20} />
          Pickup Locations
        </button>
        <button 
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={20} />
          Business Settings
        </button>
      </div>

      {message && (
        <div className={`message ${messageType}`}>
          {message}
        </div>
      )}

      <div className="profile-content">
        {/* Business Information Tab */}
        {activeTab === 'business' && (
          <div className="tab-content">
            <div className="section-header">
              <h2><Building size={24} /> Business Information</h2>
              <p>Update your business details and contact information</p>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label>Business Name</label>
                <input
                  type="text"
                  value={businessInfo.name}
                  onChange={(e) => setBusinessInfo({...businessInfo, name: e.target.value})}
                  placeholder="Enter business name"
                />
              </div>

              <div className="form-group">
                <label><Mail size={16} /> Business Email</label>
                <input
                  type="email"
                  value={businessInfo.email}
                  onChange={(e) => setBusinessInfo({...businessInfo, email: e.target.value})}
                  placeholder="business@example.com"
                />
              </div>

              <div className="form-group">
                <label><Phone size={16} /> Business Phone</label>
                <input
                  type="tel"
                  value={businessInfo.phone}
                  onChange={(e) => setBusinessInfo({...businessInfo, phone: e.target.value})}
                  placeholder="(868) 123-4567"
                />
              </div>

              <div className="form-group">
                <label><Globe size={16} /> Website</label>
                <input
                  type="url"
                  value={businessInfo.website}
                  onChange={(e) => setBusinessInfo({...businessInfo, website: e.target.value})}
                  placeholder="https://yourwebsite.com"
                />
              </div>

              <div className="form-group full-width">
                <label><MapPin size={16} /> Business Address</label>
                <input
                  type="text"
                  value={businessInfo.address.line1}
                  onChange={(e) => setBusinessInfo({
                    ...businessInfo, 
                    address: {...businessInfo.address, line1: e.target.value}
                  })}
                  placeholder="Street Address"
                />
                <input
                  type="text"
                  value={businessInfo.address.line2}
                  onChange={(e) => setBusinessInfo({
                    ...businessInfo, 
                    address: {...businessInfo.address, line2: e.target.value}
                  })}
                  placeholder="Apartment, suite, etc. (optional)"
                />
                <div className="address-row">
                  <input
                    type="text"
                    value={businessInfo.address.city}
                    onChange={(e) => setBusinessInfo({
                      ...businessInfo, 
                      address: {...businessInfo.address, city: e.target.value}
                    })}
                    placeholder="City"
                  />
                  <input
                    type="text"
                    value={businessInfo.address.postalCode}
                    onChange={(e) => setBusinessInfo({
                      ...businessInfo, 
                      address: {...businessInfo.address, postalCode: e.target.value}
                    })}
                    placeholder="Postal Code"
                  />
                </div>
                <input
                  type="text"
                  value={businessInfo.address.country}
                  onChange={(e) => setBusinessInfo({
                    ...businessInfo, 
                    address: {...businessInfo.address, country: e.target.value}
                  })}
                  placeholder="Country"
                />
              </div>

              <div className="form-group">
                <label><DollarSign size={16} /> Tax Rate (%)</label>
                <input
                  type="number"
                  step="0.01"
                  value={(businessInfo.taxRate * 100).toFixed(2)}
                  onChange={(e) => setBusinessInfo({...businessInfo, taxRate: parseFloat(e.target.value) / 100})}
                  placeholder="12.5"
                />
              </div>

              <div className="form-group">
                <label>Currency</label>
                <select
                  value={businessInfo.currency}
                  onChange={(e) => setBusinessInfo({...businessInfo, currency: e.target.value})}
                >
                  <option value="TTD">TTD - Trinidad & Tobago Dollar</option>
                  <option value="USD">USD - US Dollar</option>
                </select>
              </div>
            </div>

            <div className="form-actions">
              <button 
                className="save-btn"
                onClick={saveBusinessInfo}
                disabled={loading}
              >
                <Save size={20} />
                {loading ? 'Saving...' : 'Save Business Info'}
              </button>
            </div>
          </div>
        )}

        {/* Pickup Locations Tab */}
        {activeTab === 'pickup' && (
          <div className="tab-content">
            <div className="section-header">
              <h2><Store size={24} /> Pickup Locations</h2>
              <p>Manage where customers can pick up their orders</p>
            </div>

            <div className="pickup-locations-list">
              {pickupLocations.filter(location => location.active).map(location => (
                <div key={location.id} className="pickup-location-card">
                  <div className="location-info">
                    <div className="location-header">
                      <span className="location-icon">{location.icon}</span>
                      <h3>{location.name}</h3>
                      <div className="location-actions">
                        <button 
                          className="edit-btn"
                          onClick={() => editPickupLocation(location)}
                        >
                          <Edit2 size={16} />
                        </button>
                        <button 
                          className="delete-btn"
                          onClick={() => deletePickupLocation(location.id)}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                    <div className="location-details">
                      <p><MapPin size={14} /> {location.address}</p>
                      <p><Phone size={14} /> {location.phone}</p>
                      <p><Clock size={14} /> {location.hours}</p>
                      <p><Package size={14} /> Ready in {location.estimatedReady}</p>
                    </div>
                  </div>
                </div>
              ))}

              {pickupLocations.filter(location => location.active).length === 0 && (
                <div className="no-locations">
                  <Store size={48} />
                  <h3>No Pickup Locations</h3>
                  <p>Add your first pickup location to enable customer pickup</p>
                </div>
              )}
            </div>

            <div className="location-form-section">
              <h3>{editingLocation ? 'Edit Pickup Location' : 'Add New Pickup Location'}</h3>
              <div className="form-grid">
                <div className="form-group">
                  <label>Location Name</label>
                  <input
                    type="text"
                    value={locationForm.name}
                    onChange={(e) => setLocationForm({...locationForm, name: e.target.value})}
                    placeholder="e.g., Port of Spain Store"
                  />
                </div>

                <div className="form-group">
                  <label>Address</label>
                  <input
                    type="text"
                    value={locationForm.address}
                    onChange={(e) => setLocationForm({...locationForm, address: e.target.value})}
                    placeholder="123 Queen Street, Port of Spain"
                  />
                </div>

                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    value={locationForm.phone}
                    onChange={(e) => setLocationForm({...locationForm, phone: e.target.value})}
                    placeholder="(868) 123-4567"
                  />
                </div>

                <div className="form-group">
                  <label>Hours</label>
                  <input
                    type="text"
                    value={locationForm.hours}
                    onChange={(e) => setLocationForm({...locationForm, hours: e.target.value})}
                    placeholder="Mon-Sat: 9AM-6PM, Sun: 10AM-4PM"
                  />
                </div>

                <div className="form-group">
                  <label>Estimated Ready Time</label>
                  <input
                    type="text"
                    value={locationForm.estimatedReady}
                    onChange={(e) => setLocationForm({...locationForm, estimatedReady: e.target.value})}
                    placeholder="1-2 hours"
                  />
                </div>

                <div className="form-group">
                  <label>Icon</label>
                  <select
                    value={locationForm.icon}
                    onChange={(e) => setLocationForm({...locationForm, icon: e.target.value})}
                  >
                    <option value="🏪">🏪 Store</option>
                    <option value="🏬">🏬 Department Store</option>
                    <option value="🏢">🏢 Office</option>
                    <option value="🏭">🏭 Factory</option>
                    <option value="📦">📦 Warehouse</option>
                  </select>
                </div>
              </div>

              <div className="form-actions">
                <button 
                  className="save-btn"
                  onClick={savePickupLocation}
                  disabled={loading || !locationForm.name || !locationForm.address}
                >
                  <Save size={20} />
                  {loading ? 'Saving...' : (editingLocation ? 'Update Location' : 'Add Location')}
                </button>
                {editingLocation && (
                  <button 
                    className="cancel-btn"
                    onClick={resetLocationForm}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Business Settings Tab */}
        {activeTab === 'settings' && (
          <div className="tab-content">
            <div className="section-header">
              <h2><Settings size={24} /> Business Settings</h2>
              <p>Configure your store operations and policies</p>
            </div>

            <div className="form-grid">
              <div className="form-group">
                <label><Truck size={16} /> Free Shipping Threshold (TTD)</label>
                <input
                  type="number"
                  value={businessSettings.freeShippingThreshold}
                  onChange={(e) => setBusinessSettings({...businessSettings, freeShippingThreshold: parseFloat(e.target.value)})}
                  placeholder="0"
                />
                <small>Orders over this amount get free shipping</small>
              </div>

              <div className="form-group">
                <label>Default Shipping Rate (TTD)</label>
                <input
                  type="number"
                  value={businessSettings.defaultShippingRate}
                  onChange={(e) => setBusinessSettings({...businessSettings, defaultShippingRate: parseFloat(e.target.value)})}
                  placeholder="25"
                />
              </div>

              <div className="form-group">
                <label><Clock size={16} /> Business Hours</label>
                <input
                  type="text"
                  value={businessSettings.businessHours}
                  onChange={(e) => setBusinessSettings({...businessSettings, businessHours: e.target.value})}
                  placeholder="Mon-Sat: 9AM-6PM, Sun: 10AM-4PM"
                />
              </div>

              <div className="form-group">
                <label><Package size={16} /> Order Processing Time</label>
                <input
                  type="text"
                  value={businessSettings.orderProcessingTime}
                  onChange={(e) => setBusinessSettings({...businessSettings, orderProcessingTime: e.target.value})}
                  placeholder="1-2 business days"
                />
              </div>

              <div className="form-group">
                <label><Mail size={16} /> Contact Email</label>
                <input
                  type="email"
                  value={businessSettings.contactEmail}
                  onChange={(e) => setBusinessSettings({...businessSettings, contactEmail: e.target.value})}
                  placeholder="orders@trinitees.com"
                />
              </div>

              <div className="form-group">
                <label><Phone size={16} /> Support Phone</label>
                <input
                  type="tel"
                  value={businessSettings.supportPhone}
                  onChange={(e) => setBusinessSettings({...businessSettings, supportPhone: e.target.value})}
                  placeholder="(868) 987-6543"
                />
              </div>

              <div className="form-group full-width">
                <label>Return Policy</label>
                <textarea
                  value={businessSettings.returnPolicy}
                  onChange={(e) => setBusinessSettings({...businessSettings, returnPolicy: e.target.value})}
                  placeholder="30-day return policy"
                  rows="3"
                />
              </div>

              <div className="form-group full-width">
                <label>Refund Policy</label>
                <textarea
                  value={businessSettings.refundPolicy}
                  onChange={(e) => setBusinessSettings({...businessSettings, refundPolicy: e.target.value})}
                  placeholder="Full refund within 30 days"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={businessSettings.enablePickup}
                    onChange={(e) => setBusinessSettings({...businessSettings, enablePickup: e.target.checked})}
                  />
                  Enable Customer Pickup
                </label>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={businessSettings.enableShipping}
                    onChange={(e) => setBusinessSettings({...businessSettings, enableShipping: e.target.checked})}
                  />
                  Enable Shipping
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button 
                className="save-btn"
                onClick={saveBusinessSettings}
                disabled={loading}
              >
                <Save size={20} />
                {loading ? 'Saving...' : 'Save Settings'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminBusinessProfile;
